import React, {useState} from 'react'
import {BlockProps} from '../atoms/blockList'
import {AccordionBlockValue, AccordionBlockValueItem} from './types'
import {IconButton, Panel, FlexboxGrid, List, Row, Col, Form, SelectPicker} from 'rsuite'
import {AccordionEdit} from './accordionEdit'
import {getEmptyI18nValue} from '../utility'
import {nanoid} from 'nanoid'
import {ContentEditActionEnum} from '@dudagroup/editor/lib/client/control/contentReducer'
import {useTranslation} from 'react-i18next'

import PlusCircle from '@rsuite/icons/legacy/PlusCircle'
import Pencil from '@rsuite/icons/legacy/Pencil'
import LongArrowUp from '@rsuite/icons/legacy/LongArrowUp'
import LongArrowDown from '@rsuite/icons/legacy/LongArrowDown'
import MinusSquare from '@rsuite/icons/legacy/MinusSquare'

export type AccordionBlockProps = BlockProps<AccordionBlockValue>

export function AccordionBlock(props: AccordionBlockProps) {
  const {value, onChange, configs, languageLaneL, dispatch, path, model} = props
  const {accordion, backgroundColor} = value
  const [currentPanelId, setCurrentPanelId] = useState<string | null>(null)
  const [currentPanelIndex, setCurrentPanelIndex] = useState<number>(-1)
  const {t} = useTranslation()

  const data = (model.fields.backgroundColor as any).values.map((item: any) => {
    return {
      label: item.description,
      value: item.value,
    }
  })

  const accordionPanels = accordion.map((_item, i, array) => {
    const {content: item} = _item

    return (
      <List.Item key={i} index={i}>
        <Panel
          key={item.id}
          bodyProps={{style: {padding: '0px'}}}
          header={
            <div className="show-grid">
              <FlexboxGrid>
                <FlexboxGrid.Item
                  style={{marginTop: '0px', fontSize: '1rem', fontWeight: 'normal'}}
                  colspan={12}>
                  {item.title[languageLaneL] || '...'}
                </FlexboxGrid.Item>
                <FlexboxGrid.Item
                  colspan={12}
                  style={{
                    textAlign: 'right',
                    paddingRight: '20px',
                    marginTop: '-7px',
                  }}>
                  {i > 0 && (
                    <IconButton
                      icon={<LongArrowUp />}
                      appearance="subtle"
                      size="md"
                      onClick={(e) => {
                        e.stopPropagation()

                        dispatch({
                          type: ContentEditActionEnum.splice,
                          path: [...path, 'accordion'],
                          start: i - 1,
                          delete: 2,
                          insert: [_item, array[i - 1]],
                        })
                      }}
                      disabled={i === 0}
                    />
                  )}

                  {i < array.length - 1 && (
                    <IconButton
                      icon={<LongArrowDown />}
                      appearance="subtle"
                      size="md"
                      onClick={(e) => {
                        e.stopPropagation()

                        dispatch({
                          type: ContentEditActionEnum.splice,
                          path: [...path, 'accordion'],
                          start: i,
                          delete: 2,
                          insert: [array[i + 1], _item],
                        })
                      }}
                      disabled={i === array.length - 1}
                    />
                  )}

                  <IconButton
                    icon={<Pencil />}
                    appearance="subtle"
                    size="md"
                    onClick={() => {
                      setCurrentPanelIndex(i)
                      setCurrentPanelId(item.id)
                    }}
                  />

                  <IconButton
                    icon={<MinusSquare />}
                    appearance="subtle"
                    size="md"
                    onClick={() => {
                      dispatch({
                        type: ContentEditActionEnum.splice,
                        path: [...path, 'accordion'],
                        start: i,
                        delete: 1,
                      })
                    }}
                  />
                </FlexboxGrid.Item>
              </FlexboxGrid>
            </div>
          }></Panel>
      </List.Item>
    )
  })

  return (
    <>
      <Row className="show-grid" style={{display: 'flex', alignItems: 'center', marginBottom: 24}}>
        <Col xs={14}>
          <Form.ControlLabel style={{marginRight: 10}}>
            {t('blockList.backgroundColor')}
          </Form.ControlLabel>
          <SelectPicker
            style={{width: '100%'}}
            data={data}
            value={backgroundColor}
            searchable={false}
            onChange={(val) => {
              onChange(val, ['backgroundColor'])
            }}
          />
        </Col>
      </Row>
      <IconButton
        placement="left"
        appearance="ghost"
        icon={<PlusCircle />}
        onClick={() => {
          const accordionItem = {
            content: {
              id: nanoid(),
              title: getEmptyI18nValue(
                configs.apiConfig.languages,
                t('blockList.accordionNewPanel')
              ),
              content: [],
            } as AccordionBlockValueItem,
          }
          onChange([...accordion, accordionItem], ['accordion'])
        }}>
        {t('blockList.addAccordionGroup')}
      </IconButton>
      <br />
      <br />
      {accordionPanels.length > 0 && (
        <List
          sortable
          pressDelay={300}
          onSort={(payload) => {
            if (payload) {
              dispatch({
                type: ContentEditActionEnum.swap,
                path: [...path, 'accordion'],
                newIndex: payload.newIndex,
                oldIndex: payload.oldIndex,
              })
            }
          }}>
          {accordionPanels}
        </List>
      )}

      <AccordionEdit
        {...props}
        currentPanelId={currentPanelId}
        onClose={(val) => {
          if (val === false) {
            setCurrentPanelId(null)
            return
          }

          onChange(val, ['accordion', currentPanelIndex, 'content', 'content'])
          setCurrentPanelId(null)
        }}
      />
    </>
  )
}
